import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Save, Sparkles, Camera, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { fetchMyPreferences, updateMyPreferences, uploadMyAvatar, deleteMyAvatar } from '../../lib/meApi';
import { useAuth } from '../../contexts/AuthContext';
import LevelAvatar from './LevelAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PALETTE = [
  'emerald','amber','sky','violet','rose','indigo','teal','fuchsia','orange','lime','cyan','pink',
];

const PreferencesTab = ({ onUpdated }) => {
  const { user, refreshUser } = useAuth();
  const userLevel = user?.level || 1;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);
  const [prefs, setPrefs] = useState(null);
  const [communes, setCommunes] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, cR, kR] = await Promise.all([
          fetchMyPreferences(),
          axios.get(`${API_URL}/api/territories/communes`),
          axios.get(`${API_URL}/api/categories`),
        ]);
        setPrefs({
          username: p.username || '',
          bio: p.bio || '',
          avatar_color: p.avatar_color || 'emerald',
          avatar_url: p.avatar_url || null,
          commune_id: p.commune_id || '',
          interest_category_ids: p.interest_category_ids || [],
          name: p.name,
        });
        setCommunes(cR.data || []);
        setCategories(kR.data || []);
      } catch (e) {
        toast.error('No se pudieron cargar tus preferencias');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleInterest = (id) => {
    setPrefs(p => ({
      ...p,
      interest_category_ids: p.interest_category_ids.includes(id)
        ? p.interest_category_ids.filter(x => x !== id)
        : [...p.interest_category_ids, id],
    }));
  };

  const handlePickFile = () => fileInputRef.current?.click();

  const handleAvatarSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error('La imagen es muy grande (máx. 3 MB)');
      return;
    }
    setUploadingAvatar(true);
    try {
      const res = await uploadMyAvatar(file);
      setPrefs(p => ({ ...p, avatar_url: res.avatar_url }));
      toast.success('Foto actualizada 📸');
      refreshUser?.();
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo subir la foto');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    if (!prefs?.avatar_url) return;
    setUploadingAvatar(true);
    try {
      await deleteMyAvatar();
      setPrefs(p => ({ ...p, avatar_url: null }));
      toast.success('Foto eliminada');
      refreshUser?.();
      onUpdated?.();
    } catch {
      toast.error('No se pudo eliminar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    if (!/^[a-zA-Z0-9_.]{3,40}$/.test(prefs.username)) {
      toast.error('Usuario inválido. Usa solo letras, números, "." o "_" (3-40)');
      return;
    }
    setSaving(true);
    try {
      const res = await updateMyPreferences({
        username: prefs.username,
        bio: prefs.bio || null,
        avatar_color: prefs.avatar_color,
        commune_id: prefs.commune_id || null,
        interest_category_ids: prefs.interest_category_ids,
      });
      toast.success('Configuración guardada');
      if (res.newly_awarded_badges?.length) {
        res.newly_awarded_badges.forEach(b => {
          toast.success(`🎉 ¡Nuevo logro! ${b.emoji} ${b.name}`, { duration: 5000 });
        });
      }
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prefs) {
    return <div className="text-center py-12 text-gray-400">Cargando…</div>;
  }

  return (
    <div className="space-y-6" data-testid="me-preferences-content">
      {/* Avatar + Username header */}
      <div className="flex items-center gap-4 p-4 bg-gradient-to-br from-emerald-50 to-white rounded-2xl border border-emerald-100">
        <div className="relative shrink-0">
          <LevelAvatar
            name={prefs.name}
            color={prefs.avatar_color}
            avatarUrl={prefs.avatar_url}
            level={userLevel}
            size="lg"
          />
          <button
            type="button"
            onClick={handlePickFile}
            disabled={uploadingAvatar}
            className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-[#00CC66] hover:bg-[#00B359] text-white rounded-full p-1.5 shadow-md transition-transform hover:scale-110 disabled:opacity-60"
            data-testid="upload-avatar-btn"
            title="Cambiar foto"
          >
            {uploadingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            onChange={handleAvatarSelected}
            data-testid="avatar-file-input"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Tu perfil público</p>
          <p className="font-bold text-[#064E3B] text-lg truncate">@{prefs.username || '...'}</p>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <a
              href={`/u/${prefs.username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-emerald-600 hover:underline inline-flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" /> Ver mi perfil público
            </a>
            {prefs.avatar_url && (
              <button
                type="button"
                onClick={handleRemoveAvatar}
                disabled={uploadingAvatar}
                className="text-xs text-red-500 hover:underline inline-flex items-center gap-1"
                data-testid="remove-avatar-btn"
              >
                <Trash2 className="w-3 h-3" /> Quitar foto
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Username */}
      <div>
        <label className="block text-sm font-semibold text-[#064E3B] mb-2">Nombre de usuario</label>
        <Input
          value={prefs.username}
          onChange={(e) => setPrefs({ ...prefs, username: e.target.value.toLowerCase() })}
          placeholder="ej: nico_aventurero"
          maxLength={40}
          className="rounded-xl"
          data-testid="pref-username-input"
        />
        <p className="text-[11px] text-gray-500 mt-1">Solo letras minúsculas, números, &quot;.&quot; o &quot;_&quot;. Aparece en tu URL <code className="bg-gray-100 px-1 rounded">/u/{prefs.username || 'usuario'}</code></p>
      </div>

      {/* Bio */}
      <div>
        <label className="block text-sm font-semibold text-[#064E3B] mb-2">Bio</label>
        <Textarea
          value={prefs.bio}
          onChange={(e) => setPrefs({ ...prefs, bio: e.target.value })}
          placeholder="Cuéntale a otros qué tipo de planes te gustan..."
          maxLength={280}
          rows={3}
          className="rounded-xl resize-none"
          data-testid="pref-bio-input"
        />
        <p className="text-[11px] text-gray-500 mt-1">{prefs.bio.length}/280</p>
      </div>

      {/* Avatar color (only when no photo) */}
      {!prefs.avatar_url && (
        <div>
          <label className="block text-sm font-semibold text-[#064E3B] mb-2">Color del avatar</label>
          <div className="flex flex-wrap gap-2">
          {PALETTE.map(c => {
            const active = prefs.avatar_color === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setPrefs({ ...prefs, avatar_color: c })}
                className="transition-transform hover:scale-110"
                data-testid={`pref-color-${c}`}
                title={c}
              >
                <LevelAvatar
                  name={prefs.name}
                  color={c}
                  level={userLevel}
                  size="sm"
                  className={active ? 'ring-2 ring-offset-2 ring-emerald-500 rounded-full' : ''}
                />
              </button>
            );
          })}
        </div>
        </div>
      )}

      {/* Commune */}
      <div>
        <label className="block text-sm font-semibold text-[#064E3B] mb-2">Comuna favorita</label>
        <Select value={prefs.commune_id || 'none'} onValueChange={(v) => setPrefs({ ...prefs, commune_id: v === 'none' ? '' : v })}>
          <SelectTrigger className="rounded-xl" data-testid="pref-commune-select">
            <SelectValue placeholder="Selecciona tu comuna" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Sin preferencia —</SelectItem>
            {communes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-gray-500 mt-1">Te recomendaremos más eventos en esta comuna.</p>
      </div>

      {/* Interests */}
      <div>
        <label className="block text-sm font-semibold text-[#064E3B] mb-2">¿Qué tipo de planes te gustan?</label>
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => {
            const active = prefs.interest_category_ids.includes(cat.id);
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => toggleInterest(cat.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  active
                    ? 'bg-[#00CC66] text-white shadow'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`pref-cat-${cat.id}`}
              >
                {cat.icon ? `${cat.icon} ` : ''}{cat.name}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-gray-500 mt-2">Elige tantas como quieras. Mejorarán tus recomendaciones.</p>
      </div>

      <div className="sticky bottom-0 -mx-5 md:-mx-6 px-5 md:px-6 pt-4 pb-2 bg-gradient-to-t from-white via-white/95 to-white/0">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full rounded-full bg-[#00CC66] hover:bg-[#00B359] py-6"
          data-testid="pref-save-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </Button>
      </div>
    </div>
  );
};

export default PreferencesTab;
